/**
 * 🔗 PROCESS BLUEPRINT SERVICE
 * 
 * Serviço para gerenciar blueprints intermediários entre Mapa e Workflow.
 * 
 * Responsabilidades:
 * - CRUD de process_blueprints
 * - Mapa → Pré-Workflow (extrair etapas do mapa, criar template V9)
 * - Workflow → Mapa (ler stages V9, gerar cards VSM)
 * - Clonar blueprints
 * 
 * Regras:
 * - Nunca vincular mapa direto a workflow
 * - Sempre usar process_blueprints como intermediário
 * - Não alterar estrutura do Workflow V9
 * - Respeitar organization_id em todas queries
 */

import { supabase } from '../../../lib/supabaseClient';
import { sha256 } from '../../../lib/hashUtils';
import type {
  ProcessBlueprint,
  CreateBlueprintPayload,
  UpdateBlueprintPayload,
  BlueprintStage,
  BlueprintFormField,
  MapToWorkflowResult,
  WorkflowToMapResult,
  GeneratedMapData,
} from '../../../types/processBlueprint';
import { VALID_STATUS_TRANSITIONS } from '../../../types/processBlueprint';
import type { CardData, ExecutionField } from '../../../types/card';
import {
  buildBlueprintSnapshotSummary,
  buildBlueprintStageSnapshot,
  inferStageEngineeringFromCard,
  resolveStageEngineering,
} from './blueprintSnapshotMetrics';
import {
  BLUEPRINTS_TABLE,
  BLUEPRINTS_WRITE_TABLE,
  BLUEPRINT_WITH_LATEST_VERSION_SELECT,
  mapBlueprintRow,
} from './blueprintDataHelpers';
import {
  ensureWorkflowRoles,
  humanizeWorkflowRoleName,
  normalizeWorkflowRoleKey,
} from '../../shared/services/workflowRoleService';

// ============================================================================
// HELPERS - Conversão snake_case ↔ camelCase
// ============================================================================

const DEFAULT_MAJOR = 1;
const DEFAULT_MINOR = 0;
const DEFAULT_PATCH = 0;

// ============================================================================
// CRUD
// ============================================================================

/**
 * Lista todos os blueprints de uma organização
 */
export async function listBlueprints(organizationId: string): Promise<ProcessBlueprint[]> {
  if (!organizationId) throw new Error('organizationId é obrigatório');

  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select(BLUEPRINT_WITH_LATEST_VERSION_SELECT)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const blueprints = (data || []).map(mapBlueprintRow);

  // Somente buscar nomes dos criadores
  const creatorIds = [...new Set(blueprints.map(b => b.createdBy).filter(Boolean))] as string[];
  
  if (creatorIds.length === 0) return blueprints;

  const { data: userProfiles, error: userError } = await supabase
    .from('user_profiles')
    .select('*')
    .in('user_id', creatorIds);

  const userNameMap = new Map<string, string>();
  if (!userError && userProfiles) {
    for (const row of userProfiles as any[]) {
      userNameMap.set(row.user_id, row.full_name || row.name || row.email || row.user_id);
    }
  }

  return blueprints.map(bp => ({
    ...bp,
    createdByName: bp.createdBy ? (userNameMap.get(bp.createdBy) ?? undefined) : undefined,
  }));
}

/**
 * Busca um blueprint por ID
 */
export async function getBlueprintById(blueprintId: string): Promise<ProcessBlueprint | null> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');

  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select(BLUEPRINT_WITH_LATEST_VERSION_SELECT)
    .eq('id', blueprintId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data ? mapBlueprintRow(data) : null;
}

/**
 * Cria um novo blueprint
 */
export async function createBlueprint(payload: CreateBlueprintPayload): Promise<ProcessBlueprint> {
  if (!payload.organizationId) throw new Error('organizationId é obrigatório');
  if (!payload.sourceType) throw new Error('sourceType é obrigatório');

  const versionMajor = payload.versionMajor ?? DEFAULT_MAJOR;
  const versionMinor = payload.versionMinor ?? DEFAULT_MINOR;
  const versionPatch = payload.versionPatch ?? DEFAULT_PATCH;
  const versionSemver = payload.versionSemver ?? `${versionMajor}.${versionMinor}.${versionPatch}`;

  // 1. Insert into process_blueprints
  const { data: row, error: insertError } = await supabase
    .from(BLUEPRINTS_WRITE_TABLE)
    .insert({
      organization_id: payload.organizationId,
      project_id: payload.projectId ?? null,
      name: payload.name,
      description: payload.description ?? null,
      source_type: payload.sourceType,
      source_id: payload.sourceId ?? null,
      stages: payload.stages ?? [],
      status: payload.status ?? 'draft',
      category: payload.category ?? 'geral',
      parent_blueprint_id: payload.parentBlueprintId ?? null,
      is_corporate_standard: payload.isCorporateStandard ?? false,
      created_by: payload.createdBy ?? null,
    })
    .select('id')
    .single();

  if (insertError) throw insertError;
  if (!row?.id) throw new Error('Falha ao obter ID do blueprint criado');

  // 2. Insert initial version snapshot into process_blueprint_versions
  const snapshotStages = (payload.stages ?? []).map((stage) => buildBlueprintStageSnapshot(stage as BlueprintStage));
  const snapshotSummary = buildBlueprintSnapshotSummary(snapshotStages);
  const snapshotData = {
    name: payload.name,
    description: payload.description ?? null,
    category: payload.category ?? 'geral',
    sourceType: payload.sourceType,
    sourceId: payload.sourceId ?? null,
    status: payload.status ?? 'draft',
    stages: snapshotStages,
    ...snapshotSummary,
    metrics: {
      ...snapshotSummary,
    },
    tra: snapshotSummary.tra_standard,
    errorPercent: snapshotSummary.expected_error_percent,
    parentBlueprintId: payload.parentBlueprintId ?? null,
    isCorporateStandard: payload.isCorporateStandard ?? false,
    snapshotDate: new Date().toISOString(),
  };
  const snapshotHash = await sha256(snapshotData).catch(() => undefined);
  const versionPayload: Record<string, any> = {
    blueprint_id: row.id,
    organization_id: payload.organizationId,
    version: 1,
    version_semver: versionSemver,
    version_major: versionMajor,
    version_minor: versionMinor,
    version_patch: versionPatch,
    change_type: 'major',
    change_summary: 'Versão inicial',
    snapshot: snapshotData,
    snapshot_hash: snapshotHash ?? null,
  };

  const { error: versionError } = await supabase
    .from('process_blueprint_versions')
    .insert(versionPayload);

  if (versionError) {
    // Non-fatal: blueprint was created, version tracking just won't work
    console.warn('[createBlueprint] Failed to insert initial version:', versionError.message);
  }

  const created = await getBlueprintById(row.id);
  if (!created) {
    throw new Error('Falha ao carregar blueprint criado');
  }
  return created;
}

/**
 * Atualiza um blueprint existente
 */
export async function updateBlueprint(
  blueprintId: string,
  payload: UpdateBlueprintPayload
): Promise<ProcessBlueprint> {
  const updateData: Record<string, any> = {};
  if (payload.name !== undefined) updateData.name = payload.name;
  if (payload.description !== undefined) updateData.description = payload.description;
  if (payload.stages !== undefined) updateData.stages = payload.stages;
  if (payload.status !== undefined) updateData.status = payload.status;
  if (payload.category !== undefined) updateData.category = payload.category;
  if (payload.generatedTemplateId !== undefined) updateData.generated_template_id = payload.generatedTemplateId;
  if (payload.generatedMapData !== undefined) updateData.generated_map_data = payload.generatedMapData;
  if (payload.isCorporateStandard !== undefined) updateData.is_corporate_standard = payload.isCorporateStandard;

  const { data, error } = await supabase
    .from(BLUEPRINTS_WRITE_TABLE)
    .update(updateData)
    .eq('id', blueprintId)
    .select('id')
    .single();

  if (error) throw error;

  const updated = await getBlueprintById(data.id);
  if (!updated) {
    throw new Error('Falha ao carregar blueprint atualizado');
  }
  return updated;
}

/**
 * Verifica se o blueprint está em uso em algum projeto.
 * Retorna a contagem de projetos vinculados.
 */
export async function getBlueprintUsageCount(blueprintId: string): Promise<number> {
  if (!blueprintId) return 0;

  const { count, error } = await supabase
    .from('process_blueprint_usage')
    .select('id', { count: 'exact' })
    .eq('blueprint_id', blueprintId);

  if (error) return 0;
  return count ?? 0;
}

/**
 * Valida se a transição de status é válida.
 * Lança erro com mensagem descritiva caso inválida.
 */
export function validateStatusTransition(
  currentStatus: string,
  newStatus: string,
): void {
  const allowed = VALID_STATUS_TRANSITIONS[currentStatus as keyof typeof VALID_STATUS_TRANSITIONS];
  if (!allowed || !allowed.includes(newStatus as any)) {
    const allowedStr = allowed ? allowed.join(', ') : 'nenhum';
    throw new Error(
      `Transição inválida: ${currentStatus} → ${newStatus}. Permitido: ${allowedStr}`,
    );
  }
}

/**
 * Exclui um blueprint.
 * Verifica se o blueprint está em uso antes de excluir.
 */
export async function deleteBlueprint(blueprintId: string): Promise<void> {
  // Verificar uso antes de excluir
  const usageCount = await getBlueprintUsageCount(blueprintId);
  if (usageCount > 0) {
    throw new Error(
      `Não é possível excluir blueprint pois está em uso em ${usageCount} projeto(s). Archive-o em vez de excluir.`,
    );
  }

  const { error } = await supabase
    .from(BLUEPRINTS_WRITE_TABLE)
    .delete()
    .eq('id', blueprintId);

  if (error) throw error;
}

/**
 * Clona um blueprint existente, mantendo referência ao original
 */
export async function cloneBlueprint(
  blueprintId: string,
  organizationId: string,
  newName?: string
): Promise<ProcessBlueprint> {
  const original = await getBlueprintById(blueprintId);
  if (!original) throw new Error('Blueprint não encontrado');

  return createBlueprint({
    organizationId,
    projectId: original.projectId,
    name: newName || `${original.name} (cópia)`,
    description: original.description,
    sourceType: original.sourceType,
    sourceId: original.sourceId,
    stages: [...original.stages],
    status: 'draft',
    category: original.category,
    parentBlueprintId: original.id,
  });
}

// ============================================================================
// MAPA → PRÉ-WORKFLOW
// ============================================================================

/**
 * Converte um CardData em BlueprintStage, incluindo dados de execução.
 */
function cardToBlueprintStage(card: CardData, index: number): BlueprintStage {
  const ec = card.executionConfig;
  const cardAny = card as any;
  // Role: resources[0].role (novo) > quem > responsavel (legado)
  const primaryRole = cardAny.resources?.[0]?.role || cardAny.quem || card.responsavel || undefined;
  const primaryRoleId = cardAny.resources?.[0]?.roleId || undefined;
  return {
    order: index + 1,
    name: card.atividade || card.nome_fluxo || `Etapa ${index + 1}`,
    description: card.como || undefined,
    slaHours: ec?.slaHours ?? (card.tp ? Number(card.tp) : undefined),
    role: primaryRole,
    sourceNodeId: card.id,
    // Dados de execução (do executionConfig do card, se existir)
    instruction: ec?.instruction || undefined,
    completionType: ec?.actionType || undefined,
    formFields: ec?.fields?.map((f: ExecutionField) => ({
      id: f.id,
      label: f.label,
      type: (f.type === 'attachment' ? 'file' : f.type) as BlueprintFormField['type'],
      required: f.required ?? false,
      options: f.options?.map(o => typeof o === 'string' ? o : (o.label || o.value)),
      defaultValue: f.defaultValue,
    })) || undefined,
    exitOptions: (ec?.requireAttachment || ec?.requireJustification || ec?.requirePhoto) ? {
      requireAttachment: ec.requireAttachment,
      requireJustification: ec.requireJustification,
      requirePhoto: ec.requirePhoto,
    } : undefined,
    rules: ec?.actionType === 'approve_reject' ? { requireApproval: true } : undefined,
    ...inferStageEngineeringFromCard(card),
    departmentId: cardAny.department_id || undefined,
    metadata: {
      responsavel: primaryRole,
      roleId: primaryRoleId,
      sistema: card.sistema,
      tipo_atividade: card.tipo_atividade,
      department_id: cardAny.department_id || null,
      department_name: cardAny.setor || cardAny.sector || null,
      resources: cardAny.resources || null,
    },
    // Snapshot completo do executionConfig (inclui blocks)
    executionConfig: ec ? {
      ...ec,
      blocks: ec.blocks ?? undefined,
    } as Record<string, unknown> : undefined,
  };
}

function isActiveMapActivity(card: { tipo?: string; status?: string | null }): boolean {
  if (card?.tipo !== 'atividade') return false;
  const status = card.status;
  return status == null || status === '' || status === 'ativo';
}

/**
 * Extrai sequência lógica de etapas a partir de cards do mapa VSM.
 * 
 * Percorre a cadeia de conexões a partir do card_inicial,
 * filtra apenas atividades, e gera etapas sequenciais ordenadas.
 * Ignora posição visual — usa a estrutura de conexões.
 */
export function extractStagesFromMap(cards: CardData[]): BlueprintStage[] {
  if (!cards || cards.length === 0) return [];

  // Encontrar card inicial
  const initialCard = cards.find(c => c.tipo === 'card_inicial');
  if (!initialCard) {
    // Fallback: ordenar atividades por posição X
    return cards
      .filter(isActiveMapActivity)
      .sort((a, b) => a.posicao.x - b.posicao.x)
      .map((card, index) => cardToBlueprintStage(card, index));
  }

  // Map para lookup rápido
  const cardMap = new Map<string, CardData>();
  cards.forEach(c => cardMap.set(c.id, c));

  // Percorrer cadeia de conexões a partir do card_inicial
  const visited = new Set<string>();
  const orderedCards: CardData[] = [];

  function traverse(cardId: string) {
    if (visited.has(cardId)) return;
    visited.add(cardId);

    const card = cardMap.get(cardId);
    if (!card) return;

    // Adicionar atividades na sequência
    if (isActiveMapActivity(card)) {
      orderedCards.push(card);
    }

    // Seguir conexões de saída
    if (card.conexoes_saida) {
      for (const conn of card.conexoes_saida) {
        traverse(conn.targetId);
      }
    }
  }

  traverse(initialCard.id);

  // Converter cards ordenados em stages
  return orderedCards.map((card, index) => cardToBlueprintStage(card, index));
}

/**
 * Gera pré-workflow a partir de um mapa VSM.
 * 
 * Fluxo:
 * 1. Extrair sequência lógica dos nós (ordem estrutural)
 * 2. Criar process_blueprint
 * 3. Criar workflow_template_v9 baseado no blueprint
 * 4. Criar workflow_stages_v9 a partir da sequência
 * 5. Marcar workflow como status='draft' (não publicado)
 * 
 * NÃO cria instâncias. NÃO altera engine V9.
 */
export async function generateWorkflowFromMap(
  organizationId: string,
  projectId: string | null,
  mapId: string | null,
  mapName: string,
  cards: CardData[],
  createdBy?: string
): Promise<MapToWorkflowResult> {
  // 1. Extrair etapas do mapa
  const stages = extractStagesFromMap(cards);
  if (stages.length === 0) {
    throw new Error('Nenhuma atividade encontrada no mapa para gerar workflow');
  }

  // 2. Criar blueprint
  const blueprint = await createBlueprint({
    organizationId,
    projectId,
    name: `Blueprint: ${mapName}`,
    description: `Gerado automaticamente a partir do mapa "${mapName}"`,
    sourceType: 'map',
    sourceId: mapId || undefined,
    stages,
    status: 'draft',
    createdBy,
  });

  // 3. Criar workflow_template_v9 (somente draft, NÃO publicado)
  const { data: template, error: templateError } = await supabase
    .from('workflow_templates_v9')
    .insert({
      organization_id: organizationId,
      name: `[Draft] ${mapName}`,
      description: `Pré-workflow gerado a partir do mapa "${mapName}" via blueprint`,
      category: 'processo',
      is_active: false,  // draft, não ativo
      version: 1,
      require_sequential_completion: true,
      auto_assign_strategy: 'manual',
      created_by: createdBy ?? null,
      metadata: {
        blueprint_id: blueprint.id,
        source_map_id: mapId || null,
        generated_at: new Date().toISOString(),
      },
    })
    .select('id')
    .single();

  if (templateError) throw templateError;

  const stageRoleMap = await ensureWorkflowRoles(
    organizationId,
    stages.map((stage) => stage.role ?? stage.metadata?.responsavel ?? null),
  );

  // 4. Criar workflow_stages_v9 a partir das etapas do blueprint
  const stageInserts = stages.map(stage => {
    const stageEngineering = resolveStageEngineering(stage);
    const roleLike = stage.role ?? stage.metadata?.responsavel ?? null;
    const roleRecord = roleLike
      ? stageRoleMap.get(normalizeWorkflowRoleKey(roleLike) ?? '')
      : null;
    return {
      template_id: template.id,
      organization_id: organizationId,
      name: stage.name,
      description: stage.description ?? null,
      stage_order: stage.order,
      sla_hours: stage.slaHours ?? null,
      sla_business_hours: false,
      warning_threshold_percent: stageEngineering.warningThresholdPercent,
      require_approval: stageEngineering.requireApproval,
      can_skip: false,
      responsible_role_id: roleRecord?.id ?? null,
      default_assignee_role: roleLike,
      department_id: stage.departmentId ?? stage.metadata?.department_id ?? null,
      wip_limit: stageEngineering.wipLimit,
      metadata: {
        source_node_id: stage.sourceNodeId,
        sistema: stage.metadata?.sistema,
        tipo_atividade: stage.metadata?.tipo_atividade,
        department_id: stage.departmentId ?? stage.metadata?.department_id ?? null,
        department_name: stage.metadata?.department_name ?? null,
        tp_standard: stageEngineering.tpStandard,
        tra_standard: stageEngineering.traStandard,
        expected_error_percent: stageEngineering.expectedErrorPercent,
        correction_time_standard: stageEngineering.correctionTimeStandard,
        warning_threshold_percent: stageEngineering.warningThresholdPercent,
        wip_limit: stageEngineering.wipLimit,
        require_approval: stageEngineering.requireApproval,
        role: roleRecord?.roleKey ?? roleLike ?? null,
        roleId: roleRecord?.id ?? null,
        roleName: roleRecord?.name ?? humanizeWorkflowRoleName(roleLike) ?? null,
      },
    };
  });

  const { error: stagesError } = await supabase
    .from('workflow_stages_v9')
    .insert(stageInserts);

  if (stagesError) throw stagesError;

  // 5. Atualizar blueprint com referência ao template gerado
  await updateBlueprint(blueprint.id, {
    generatedTemplateId: template.id,
    status: 'published',
  });

  return {
    blueprint: { ...blueprint, generatedTemplateId: template.id, status: 'published' },
    templateId: template.id,
    stagesCreated: stages.length,
  };
}

// ============================================================================
// WORKFLOW → MAPA
// ============================================================================

/**
 * Gera esqueleto de mapa VSM a partir de um workflow template.
 * 
 * Fluxo:
 * 1. Ler workflow_stages_v9 do template
 * 2. Gerar nós (cards) automaticamente no formato VSM
 * 3. Criar conexões sequenciais
 * 4. Definir generated_from_workflow = true
 * 5. NÃO alterar workflow_template
 */
export async function generateMapFromWorkflow(
  organizationId: string,
  projectId: string | null,
  templateId: string,
  templateName: string,
  createdBy?: string
): Promise<WorkflowToMapResult> {
  // 1. Carregar stages do template
  const { data: stagesData, error: stagesError } = await supabase
    .from('workflow_stages_v9')
    .select('*')
    .eq('template_id', templateId)
    .eq('organization_id', organizationId)
    .order('stage_order');

  if (stagesError) throw stagesError;
  if (!stagesData || stagesData.length === 0) {
    throw new Error('Nenhuma etapa encontrada no workflow template');
  }

  // 2. Converter stages em BlueprintStages
  const blueprintStages: BlueprintStage[] = stagesData.map(stage => ({
    order: stage.stage_order,
    name: stage.name,
    description: stage.description ?? undefined,
    slaHours: stage.sla_hours ? Number(stage.sla_hours) : undefined,
    sourceNodeId: stage.id,
    tpStandard: stage.sla_hours ? Number(stage.sla_hours) * 60 : undefined,
    warningThresholdPercent: stage.warning_threshold_percent ?? undefined,
    wipLimit: stage.wip_limit ?? undefined,
    requireApproval: stage.require_approval ?? undefined,
    rules: stage.require_approval ? { requireApproval: true } : undefined,
    metadata: {
      default_assignee_role: stage.default_assignee_role,
      wip_limit: stage.wip_limit,
    },
  }));

  // 3. Gerar cards VSM automaticamente
  const CARD_SPACING_X = 320;
  const CARD_START_X = 100;
  const CARD_Y = 200;

  // Card inicial
  const initialCardId = crypto.randomUUID();
  // Card final
  const finalCardId = crypto.randomUUID();

  const generatedCards: GeneratedMapData['cards'] = [];
  const generatedConnections: GeneratedMapData['connections'] = [];

  // Card inicial
  generatedCards.push({
    id: initialCardId,
    nome_fluxo: templateName,
    atividade: 'Início',
    posicao: { x: CARD_START_X, y: CARD_Y },
    tipo: 'card_inicial',
  });

  // Gerar um card de atividade para cada stage
  let prevCardId = initialCardId;

  stagesData.forEach((stage, index) => {
    const cardId = crypto.randomUUID();
    generatedCards.push({
      id: cardId,
      atividade: stage.name,
      posicao: { x: CARD_START_X + (index + 1) * CARD_SPACING_X, y: CARD_Y },
      tipo: 'atividade',
      tp: stage.sla_hours ? Number(stage.sla_hours) * 60 : undefined,
      lt: stage.sla_hours ? Number(stage.sla_hours) * 60 : undefined,
      responsavel: stage.default_assignee_role ?? undefined,
    });

    // Conexão do card anterior para este
    generatedConnections.push({
      sourceId: prevCardId,
      targetId: cardId,
    });

    prevCardId = cardId;
  });

  // Card final
  generatedCards.push({
    id: finalCardId,
    atividade: 'Fim',
    posicao: { x: CARD_START_X + (stagesData.length + 1) * CARD_SPACING_X, y: CARD_Y },
    tipo: 'card_final',
  });

  // Conexão do último stage para card final
  generatedConnections.push({
    sourceId: prevCardId,
    targetId: finalCardId,
  });

  const mapData: GeneratedMapData = {
    cards: generatedCards,
    connections: generatedConnections,
    generatedFromWorkflow: true,
  };

  // 4. Criar blueprint
  const blueprint = await createBlueprint({
    organizationId,
    projectId,
    name: `Blueprint: ${templateName}`,
    description: `Gerado automaticamente a partir do workflow "${templateName}"`,
    sourceType: 'workflow',
    sourceId: templateId,
    stages: blueprintStages,
    status: 'published',
    createdBy,
  });

  // 5. Atualizar blueprint com dados do mapa gerado
  await updateBlueprint(blueprint.id, {
    generatedMapData: mapData,
  });

  return {
    blueprint: { ...blueprint, generatedMapData: mapData },
    mapData,
  };
}
